import * as React from 'react';
import * as Yup from 'yup';

import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Formik, FormikActions, FormikProps } from 'formik';
import { mergeFields, processApiError } from 'common/utils/form';

import { AppState } from 'common/models/AppState';
import { MemberApi } from 'common/api/resources/MemberApi';
import { Member } from 'common/api/models/Member';
import { FormInput } from 'common/components/form/FormInput';
import moment = require('moment');

interface MemberDialogProps {
  member?: Member;
  isOrg: boolean;
  appState: AppState;
  onSave: (id: string) => void;
  onCancel: () => void;
}
class MemberFormValues {
  name = '';
  slug = '';
  memberType = 'MEMB';
  wiki = 'NONE';
  wikiExpireDate = '';
  certificationExpireDate = '';
  globalConnect = 'NONE';
  globalConnectExpireDate = '';
  paidBranchCount = 0;
  expireDate = '';
  renewType = 'BILL';
}

export class MemberDialog extends React.Component<MemberDialogProps> {
  validationSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    slug: Yup.string()
      .matches(/[a-z0-9-/]+/, 'Invalid slug pattern. Must be lowercase letters and hyphen.')
      .required('Required'),
    memberType: Yup.string().required('Required'),
    paidBranchCount: Yup.number().min(0),
    wiki: Yup.string(),
    wikiExpireDate: Yup.date().when('wiki', {
      is: (wiki: any) => wiki === 'ACCESS',
      then: Yup.date().required('Required'),
    }),
    expireDate: Yup.date().when('memberType', {
      is: (memberType: any) => memberType !== 'PROSPECT' && memberType !== 'NONE' && memberType !== 'EM',
      then: Yup.date().required('Required'),
    }),
    globalConnect: Yup.string(),
    // globalConnectExpireDate: Yup.date().when('globalConnect', {
    //  is: (globalConnect: any) => globalConnect === 'ACCESS',
    //  then: Yup.date().required('Required'),
    // }),
  });

  initialValues = () => {
    return this.props.member
      ? mergeFields(new MemberFormValues(), {
          ...this.props.member,
          ...this.props.member.vars,
        })
      : new MemberFormValues();
  };

  handleSubmit = (values: MemberFormValues, actions: FormikActions<MemberFormValues>) => {
    const member = this.props.member!;
    let memberData: Member = {
      name: values.name,
      slug: values.slug,
      social: {},
      memberType: Member.MemberType[values.memberType],
      paidBranchCount: values.paidBranchCount,
      wikiExpireDate: values.wikiExpireDate && values.wiki === 'NONE' ? '' : values.wikiExpireDate,
      certificationExpireDate: values.certificationExpireDate,
      expireDate: values.expireDate ? values.expireDate : '',
      vars: {
        wiki: values.wiki,
        globalConnect: values.globalConnect,
        globalConnectExpireDate:
          values.globalConnect === 'NONE'
            ? ''
            : values.globalConnectExpireDate
              ? moment(values.globalConnectExpireDate)
                  .utc()
                  .hour(0)
                  .toISOString()
              : '',
      },
      renewType: Member.RenewType[values.renewType],
    };

    if (!member.id) {
      memberData = {
        ...memberData,
        type: this.props.isOrg ? Member.Type.ORG : Member.Type.USER,
      };
    }

    const call = member.id ? MemberApi.updateMember(member.id, memberData) : MemberApi.createMember(memberData);
    call.then(
      res => {
        this.props.onSave(member.id ? member.id : (res as any).id);
        actions.setSubmitting(false);
        actions.setStatus(undefined);
      },
      err => {
        processApiError(err, actions);
      }
    );
  };

  render() {
    const props = this.props;
    const isOrg = props.isOrg;
    // const isSuperAdmin = isSuperuser(this.props.member!);
    const isBranch = props.member && props.member.parentOrgId;
    if (props.member && props.member.vars && props.member.vars.globalConnectExpireDate) {
      props.member.vars.globalConnectExpireDate = moment(props.member.vars.globalConnectExpireDate)
        .utc()
        .format('YYYY-MM-DD');
    }
    const title = props.member && props.member.id ? 'Edit Membership' : 'Add Membership';
    return (
      <Modal size="lg" isOpen={!!props.member} toggle={props.onCancel} keyboard={true}>
        <Formik
          initialValues={this.initialValues()}
          validationSchema={this.validationSchema}
          onSubmit={this.handleSubmit}
          render={(form: FormikProps<MemberFormValues>) => (
            <form onSubmit={form.handleSubmit}>
              <ModalHeader toggle={this.props.onCancel}>{title}</ModalHeader>
              <ModalBody>
                <div className="row">
                  <div className="col">
                    <FormInput label="Profile Name" name="name" id="name" />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormInput label="Profile Slug" name="slug" id="slug" autoCapitalize="none" />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormInput label="Member Type" name="memberType" id="memberType" tag="select">
                      {isOrg && <option value="MEMB">MEMB (Member Company)</option>}
                      {isOrg && isBranch && <option value="IBRANCH">IBRANCH (Independently Billed Branch)</option>}
                      {isOrg && <option value="SCHOOL">SCHOOL (School))</option>}
                      {isOrg && <option value="IP">IP (Industry Partner Company)</option>}
                      {isOrg && <option value="BRANCH">BRANCH (Branch)</option>}
                      {!isOrg && <option value="PROSPECT">PROSPECT (Prospect)</option>}
                      {!isOrg && <option value="IM">IM (Individual Member)</option>}
                      {!isOrg && <option value="EM">EM (Individual Member of Member Co)</option>}
                      {!isOrg && <option value="STUDENT">STUDENT (Student)</option>}
                      {!isOrg && <option value="RG">RG (Recent Graduate Professional)</option>}
                      {!isOrg && <option value="HON">HON (Past President)</option>}
                      {!isOrg && <option value="RETIRED">RETIRED (Retired)</option>}
                      <option value="NONE">NONE (Non Member)</option>
                    </FormInput>
                  </div>
                </div>
                {!isOrg && (
                  <div className="row">
                    <div className="col">
                      <FormInput
                        id="wiki"
                        name="wiki"
                        label="Wiki Access"
                        tag="select"
                        value={form.values.wiki}
                        onChange={form.handleChange}
                      >
                        <option value="NONE">No Access</option>
                        <option value="ACCESS">Access</option>
                        <option value="MODERATOR">Moderator</option>
                      </FormInput>
                    </div>
                  </div>
                )}
                {form.values.wiki &&
                  form.values.wiki === 'ACCESS' &&
                  !isOrg &&
                  this.props.appState.user &&
                  this.props.appState.user.adminAccess ===
                    (Member.AdminAccess.SUPERUSER || Member.AdminAccess.ADMIN) && (
                    <div className="row">
                      <div className="col">
                        <FormInput label="Wiki Expire Date" name="wikiExpireDate" id="wikiExpireDate" type="date" />
                      </div>
                    </div>
                  )}
                {!isOrg && (
                  <div className="row">
                    <div className="col">
                      <FormInput
                        id="globalConnect"
                        name="globalConnect"
                        label="Global Connect Access"
                        tag="select"
                        value={form.values.globalConnect}
                        onChange={form.handleChange}
                      >
                        <option value="NONE">No Access</option>
                        <option value="ACCESS">Access</option>
                      </FormInput>
                    </div>
                  </div>
                )}

                {form.values.globalConnect &&
                  form.values.globalConnect === 'ACCESS' && (
                    <div className="row">
                      <div className="col">
                        <FormInput
                          id="globalConnectExpireDate"
                          name="globalConnectExpireDate"
                          label="Global Connect Renewal Date"
                          type="date"
                        />
                      </div>
                    </div>
                  )}
                <div className="row">
                  <div className="col">
                    <FormInput label="Renewal Date" name="expireDate" id="expireDate" type="date" />
                  </div>
                </div>

                <div className="row">
                  <div className="col col-3">
                    <FormInput
                      label="Renewal Type"
                      name="renewType"
                      id="renewType"
                      value={form.values.renewType}
                      onChange={form.handleChange}
                      tag="select"
                    >
                      <option value="BILL">Invoice</option>
                      <option value="AUTO">Auto-Renew</option>
                      <option value="MANUAL">Manual</option>
                    </FormInput>
                  </div>
                </div>

                <div className="row">
                  <div className="col">
                    <FormInput
                      label="Certification Renewal Date"
                      name="certificationExpireDate"
                      id="certificationExpireDate"
                      type="date"
                    />
                  </div>
                </div>

                {isOrg && (
                  <div className="row">
                    <div className="col col-3">
                      <FormInput label="Paid Branches" name="paidBranchCount" id="paidBranchCount" type="number" />
                    </div>
                  </div>
                )}
                {form.status && <div className="alert alert-danger">{form.status}</div>}
              </ModalBody>
              <ModalFooter>
                <div>
                  <Button color="secondary" onClick={this.props.onCancel}>
                    Cancel
                  </Button>{' '}
                  <Button color="primary" type="submit">
                    Save
                  </Button>
                </div>
              </ModalFooter>
            </form>
          )}
        />
      </Modal>
    );
  }
}



// WEBPACK FOOTER //
// ./src/common/components/dialogs/MemberDialog.tsx